import * as React from 'react';

/*
Note: Please avoid importing components directly from the Shared Library.
Instead, create a parent container that renders the shared component directly.
This approach allows you to pass the site property to the shared components, ensuring that the shared library component is
aware of the current site during SSR.
*/

const KeyFeatures = React.lazy(
  () =>
    import(
      /* webpackChunkName: "KeyFeatures-Chunk" */ './organisms/KeyFeatures/KeyFeaturesWrapper'
    ),
);

const HighlightModule = React.lazy(
  () =>
    import(
      /* webpackChunkName: "HighlightModule-Chunk" */ './organisms/HighlightModule/HighlightModuleWrapper'
    ),
);

const Header = React.lazy(
  () =>
    import(/* webpackChunkName: "Header-Chunk" */ './organisms/Header/Header'),
);

const Footer = React.lazy(
  () =>
    import(
      /* webpackChunkName: "Footer-Chunk" */ './organisms/Footer/FooterWrapper'
    ),
);

const InspirationCard = React.lazy(
  () =>
    import(
      /* webpackChunkName: "InspirationCard-Chunk" */ './organisms/InspirationCard/InspirationCardWrapper'
    ),
);

const ProductCard = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ProductCard-Chunk" */ './molecules/ProductCardWrapper/ProductCardWrapper'
    ),
);

const PageTitle = React.lazy(
  () =>
    import(
      /* webpackChunkName: "PageTitle-Chunk" */ './organisms/PageTitle/PageTitleWrapper'
    ),
);

const ExpandOnScrollVideo = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ExpandOnScrollVideo-Chunk" */ './organisms/ExpandingVideo/ExpandingVideo'
    ),
);

const IntroductionModule = React.lazy(
  () =>
    import(
      /* webpackChunkName: "IntroductionModule-Chunk" */ './organisms/IntroductionModule/IntroductionModule'
    ),
);

const FullWidthMedia = React.lazy(
  () =>
    import(
      /* webpackChunkName: "FullWidthMedia-Chunk" */ './organisms/FullWidthMedia/FullWidthMediaWrapper'
    ),
);

const HotspotCards = React.lazy(
  () =>
    import(
      /* webpackChunkName: "HotspotCards-Chunk" */ './organisms/HotspotCards/HotspotCards'
    ),
);

const AnchorMenu = React.lazy(
  () =>
    import(
      /* webpackChunkName: "AnchorMenu-Chunk" */ './organisms/AnchorMenu/AnchorMenu'
    ),
);

const ContentCards = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ContentCards-Chunk" */ './organisms/ContentCards/ContentCardsWrapper'
    ),
);

const StorytellingModule = React.lazy(
  () =>
    import(
      /* webpackChunkName: "StorytellingModule-Chunk" */ './organisms/StorytellingModule/StorytellingModuleWrapper'
    ),
);

const ColourCardGrid = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ColourCardGrid-Chunk" */ './organisms/ColourSwatchCardGrid/ColourSwatchCardGrid'
    ),
);

const ColourCardCarousel = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ColourCardCarousel-Chunk" */ './organisms/ColourCardCarousel/ColourCardCarousel'
    ),
);

const HeadlineAndText = React.lazy(
  () =>
    import(
      /* webpackChunkName: "HeadlineAndText-Chunk" */ './organisms/HeadlineAndTextBlock/HeadlineAndTextBlockWrapper'
    ),
);

const ServiceAccordion = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ServiceAccordion-Chunk" */ './organisms/ServiceAccordion/ServiceAccordionWrapper'
    ),
);

const ProductCarousel = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ProductCarousel-Chunk" */ './organisms/ProductCarousel/ProductCarousel'
    ),
);

const HotspotFeature = React.lazy(
  () =>
    import(
      /* webpackChunkName: "HotspotFeature-Chunk" */ './organisms/HotspotFeature/HotspotFeature'
    ),
);

const IconCards = React.lazy(
  () =>
    import(
      /* webpackChunkName: "IconCards-Chunk" */ './organisms/IconCards/IconCards'
    ),
);

const Breadcrumb = React.lazy(
  () =>
    import(
      /* webpackChunkName: "Breadcrumb-Chunk" */ './organisms/PageBreadcrumb/PageBreadcrumbWrapper'
    ),
);

const WishlistListing = React.lazy(
  () =>
    import(
      /* webpackChunkName: "WishlistListing-Chunk" */ './organisms//WishlistListing/WishlistListing'
    ),
);

const WishlistGrid = React.lazy(
  () =>
    import(
      /* webpackChunkName: "WishlistGrid-Chunk" */ './organisms/WishlistGrid/WishlistGrid'
    ),
);

const PageTitleAnimationHandler = React.lazy(
  () =>
    import(
      /* webpackChunkName: "PageTitleAnimationHandler-Chunk" */ './organisms/PageTitleAnimationHandler/PageTitleAnimationHandler'
    ),
);

const ContentExpander = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ContentExpander-Chunk" */ './organisms/ContentExpander/ContentExpanderWrapper'
    ),
);

const ColourDetailInfo = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ColourDetailInfo-Chunk" */ './organisms/ColourDetailInfo/ColourDetailInfo'
    ),
);

const ProductDetailInfoSection = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ProductDetailInfoSection-Chunk" */ './organisms/ProductDetailHero/ProductDetailHero'
    ),
);

const ColourListing = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ColourListing-Chunk" */ './organisms/ColourListing/ColourListing'
    ),
);

const ProductListing = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ProductListing-Chunk" */ './organisms/ProductListing/ProductListing'
    ),
);

const MediaHero = React.lazy(
  () =>
    import(
      /* webpackChunkName: "MediaHero-Chunk" */ './organisms/MediaHero/MediaHeroWrapper'
    ),
);

const TechnicalDownload = React.lazy(
  () =>
    import(
      /* webpackChunkName: "TechnicalDownload-Chunk" */ './organisms/TechnicalDownload/TechnicalDownload'
    ),
);

const Newsletter = React.lazy(
  () =>
    import(
      /* webpackChunkName: "Newsletter-Chunk" */ './organisms/NewsletterSignup/NewsletterSignupWrapper'
    ),
);

const TextBlock = React.lazy(
  () =>
    import(
      /* webpackChunkName: "TextBlock-Chunk" */ './organisms/TextBlock/TextBlockWrapper'
    ),
);

const ColourScheme = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ColourScheme-Chunk" */ './organisms/ColourSchemeBlock/ColourSchemeBlock'
    ),
);

const InspirationDetailListing = React.lazy(
  () =>
    import(
      /* webpackChunkName: "InspirationDetailListing-Chunk" */ './organisms/InspirationDetailListing/InspirationDetailListing'
    ),
);

const ArticleHeader = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ArticleHeader-Chunk" */ './organisms/ArticleHeaderWrapper/ArticleHeaderWrapper'
    ),
);

const ArticleStamp = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ArticleStamp-Chunk" */ './organisms/ArticleStampWrapper/ArticleStampWrapper'
    ),
);

const AuthorBio = React.lazy(
  () =>
    import(
      /* webpackChunkName: "AuthorBio-Chunk" */ './organisms/AuthorBio/AuthorBio'
    ),
);

const ProductCardBlock = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ProductCardBlock-Chunk" */ './organisms/ArticleProductBlock/ArticleProductBlock'
    ),
);

const TwoColumnHotspotColourImage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "TwoColumnHotspotColourImage-Chunk" */ './organisms/TwoColumnHotspotColourImage/TwoColumnHotspotColourImage'
    ),
);

const ArticleQuoteBlock = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ArticleQuoteBlock-Chunk" */ './organisms/ArticleQuoteBlock/ArticleQuoteBlock'
    ),
);

const FullWidthHotspotCard = React.lazy(
  () =>
    import(
      /* webpackChunkName: "FullWidthHotspotCard-Chunk" */ './organisms/FullWidthHotspotCard/FullWidthHotspotCard'
    ),
);

const LinkCard = React.lazy(
  () =>
    import(
      /* webpackChunkName: "LinkCard-Chunk" */ './organisms/LinkCardBlockWrapper/LinkCardBlockWrapper'
    ),
);

const FAQBlock = React.lazy(
  () =>
    import(
      /* webpackChunkName: "FAQBlock-Chunk" */ './organisms/FAQBlock/FAQBlock'
    ),
);

export default {
  AnchorMenu,
  ArticleHeader,
  ArticleQuoteBlock,
  ArticleStamp,
  AuthorBio,
  Breadcrumb,
  ColourCardCarousel,
  ColourCardGrid,
  ColourDetailInfo,
  ColourListing,
  ColourScheme,
  ContentCards,
  ContentExpander,
  ExpandOnScrollVideo,
  FAQBlock,
  Footer,
  FullWidthHotspotCard,
  FullWidthMedia,
  Header,
  HeadlineAndText,
  HighlightModule,
  HotspotCards,
  HotspotFeature,
  IconCards,
  InspirationCard,
  InspirationDetailListing,
  IntroductionModule,
  KeyFeatures,
  LinkCard,
  MediaHero,
  Newsletter,
  PageTitle,
  PageTitleAnimationHandler,
  ProductCard,
  ProductCardBlock,
  ProductCarousel,
  ProductDetailInfoSection,
  ProductListing,
  ServiceAccordion,
  StorytellingModule,
  TechnicalDownload,
  TextBlock,
  TwoColumnHotspotColourImage,
  WishlistGrid,
  WishlistListing,
};
